import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { PasswordResetIn, UserCreateIn, passwordResetInPasswordMax, userCreateInDisplayNameOneMax, userCreateInPasswordOneMax, userUpdateInEmailOneMax } from '@/api/generated/schemas';
import { usePermissionMatrix, useResetPassword } from '@/api/queries';
import type { AuthSource, UserAccount } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { useSession } from '@/session/SessionContext';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { FieldLabel, InfoTip } from '@/ui/FieldLabel';
import { ROLES, roleLabel } from '@/ui/format';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { ActionButton, buttonState } from '@/ui/ActionButton';

import { ADMIN_ONLY_REASON, useUserDialog } from './useUserDialog';

/** The typed fields by API path, with their inputs' ids: each shows its 422 message beside it, the banner the rest. */
const INPUT_IDS = { email: 'user-email', display_name: 'user-name', password: 'user-password' } as const satisfies Partial<
  Record<keyof typeof UserCreateIn.shape, string>
>;
const PLACED = Object.keys(INPUT_IDS);

const PASSWORD_HINT = 'At least 10 characters, mixing letters with digits or symbols.';

type UserForm = ReturnType<typeof useUserDialog>;

const LOCAL_SUBTITLE = 'The account can sign in immediately with the password you set.';
const ENTRA_SUBTITLE = 'They sign in with Microsoft. Nothing to send them.';

/** Add (no `user`) or edit (name, Source and roles) an account. A field the
 *  caller may not change is shown read-only. */
export function UserDialog({
  user,
  isCaller = false,
  onClose,
}: {
  user?: UserAccount;
  isCaller?: boolean;
  onClose: () => void;
}) {
  const form = useUserDialog(user, isCaller, onClose);
  const adding = !user;

  if (user && form.asking === 'source') {
    return (
      <ConfirmDialog
        title="Switch to Entra ID?"
        confirmLabel="Switch to Entra ID"
        pending={form.mutation.isPending}
        error={form.mutation.error}
        onClose={form.cancelAsk}
        onConfirm={form.agree}
      >
        <p>
          <b>{user.display_name}</b>: their password will be removed and they will sign in with Microsoft. They are signed
          out everywhere.
        </p>
      </ConfirmDialog>
    );
  }

  if (user && form.asking === 'access') {
    return (
      <ConfirmDialog
        title="Take away all access?"
        confirmLabel="Take away access"
        danger
        pending={form.mutation.isPending}
        error={form.mutation.error}
        onClose={form.cancelAsk}
        onConfirm={form.agree}
      >
        <p>
          <b>{user.display_name}</b> keeps the account but can do nothing until someone gives a role.
        </p>
      </ConfirmDialog>
    );
  }

  return (
    <Modal
      title={user ? 'Edit user' : 'Add user'}
      subtitle={user ? user.email : form.source === 'local' ? LOCAL_SUBTITLE : ENTRA_SUBTITLE}
      onClose={onClose}
      onSubmit={form.submit}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(form.mutation)} label={user ? 'Save changes' : 'Add user'} disabled={form.incomplete} />
        </>
      }
    >
      <UserFields form={form} adding={adding} />
    </Modal>
  );
}

/** A field's hint, only when there is something to say; its control names it in aria-describedby. */
function Hint({ id, text }: { id: string; text: string | null }) {
  return text ? <span className="hint" id={id}>{text}</span> : null;
}

/** The aria-describedby for a control whose hint may be absent. */
function describedBy(id: string, text: string | null): string | undefined {
  return text ? id : undefined;
}

const ENTRA_EMAIL_HINT = 'Their GFT e-mail or UPN. Their name comes from Microsoft at first sign-in.';

/** Source first: it decides which fields apply. The same order in Add and Edit. */
function UserFields({ form, adding }: { form: UserForm; adding: boolean }) {
  // Why the field is locked is a state fact and stays visible; how to fill it is an info tip.
  const emailTip = form.emailHint === null && adding && form.source === 'entra' ? ENTRA_EMAIL_HINT : undefined;
  return (
    <div className="stack-md">
      <FormError error={bannerError(form.mutation.error, PLACED)} />
      <SourceField form={form} />
      <div className="field">
        <FieldLabel htmlFor={INPUT_IDS.email} id="user-email-tip" label="E-mail" hint={emailTip} />
        <input
          id={INPUT_IDS.email}
          type="email"
          required
          autoFocus={adding}
          value={form.email}
          maxLength={userUpdateInEmailOneMax}
          placeholder="name@gft.com"
          readOnly={form.emailHint !== null}
          {...fieldA11y(INPUT_IDS.email, form.server.email, describedBy('user-email-hint', form.emailHint) ?? (emailTip ? 'user-email-tip' : undefined))}
          onChange={(e) => form.setEmail(e.target.value)}
        />
        <Hint id="user-email-hint" text={form.emailHint} />
        <FieldError inputId={INPUT_IDS.email} message={form.server.email} />
      </div>
      {form.showName && (
        <div className="field">
          <label htmlFor={INPUT_IDS.display_name}>Display name</label>
          <input
            id={INPUT_IDS.display_name}
            type="text"
            required
            maxLength={userCreateInDisplayNameOneMax}
            value={form.name}
            readOnly={form.nameHint !== null}
            {...fieldA11y(INPUT_IDS.display_name, form.server.display_name, describedBy('user-name-hint', form.nameHint))}
            onChange={(e) => form.setName(e.target.value)}
          />
          <Hint id="user-name-hint" text={form.nameHint} />
          <FieldError inputId={INPUT_IDS.display_name} message={form.server.display_name} />
        </div>
      )}
      {form.needsPassword && <PasswordField form={form} adding={adding} />}
      <RoleField form={form} />
    </div>
  );
}

const SOURCE_CHOICES: { value: AuthSource; label: string }[] = [
  { value: 'entra', label: 'Entra ID' },
  { value: 'local', label: 'Local' },
];

/** One segmented choice, the same in Add and Edit. */
function SourceField({ form }: { form: UserForm }) {
  return (
    <div className="field">
      <span className="field-label">
        <span className="label" id="user-source-label">Source</span>
        {form.canChangeSource && form.sourceHint && <InfoTip id="user-source-hint" about="Source">{form.sourceHint}</InfoTip>}
      </span>
      <div
        className="segmented"
        role="radiogroup"
        aria-labelledby="user-source-label"
        aria-describedby={describedBy('user-source-hint', form.sourceHint)}
      >
        {SOURCE_CHOICES.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={form.source === choice.value}
            disabled={!form.canChangeSource}
            onClick={() => form.setSource(choice.value)}
          >
            {choice.label}
          </button>
        ))}
      </div>
      {/* Why the choice is locked is a state fact: visible text, not a tip. */}
      <Hint id="user-source-hint" text={form.canChangeSource ? null : form.sourceHint} />
      {form.switching && (
        <div className="alert alert-warning" role="alert">
          <AlertTriangle aria-hidden="true" />
          <div className="a-body">Changing the source signs this person out everywhere.</div>
        </div>
      )}
    </div>
  );
}

/** The roles a person holds, as checkboxes like a token's scopes; each carries the catalog's one-line description. */
function RoleField({ form }: { form: UserForm }) {
  const { can } = useSession();
  const matrix = usePermissionMatrix({ enabled: can('access.permissions.view') });
  const described = new Map((matrix.data?.roles ?? []).map((r) => [r.key, r.description]));
  return (
    <fieldset className="field-group" disabled={!form.canChangeRole} aria-describedby={describedBy('user-role-hint', form.hint)}>
      <legend className="label">Roles</legend>
      <ul className="check-list">
        {ROLES.map((role) => {
          const locked = role === 'admin' && form.adminLocked;
          const hint = [described.get(role), locked ? ADMIN_ONLY_REASON : undefined].filter(Boolean).join(' ');
          return (
            <li key={role}>
              <label className="check role-check">
                <input
                  type="checkbox"
                  checked={form.roles.includes(role)}
                  disabled={locked}
                  onChange={(e) => form.toggleRole(role, e.target.checked)}
                />
                <span className="ck-body">
                  {roleLabel(role)} {hint && <span className="hint">({hint})</span>}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {/* Why the roles are locked or incomplete is a state fact: visible text, not a tip. */}
      {form.clearsAccess ? (
        <div className="alert alert-warning" role="status" id="user-role-hint">
          <AlertTriangle aria-hidden="true" />
          <div className="a-body">{form.hint}</div>
        </div>
      ) : (
        <Hint id="user-role-hint" text={form.hint} />
      )}
    </fieldset>
  );
}

function PasswordField({ form, adding }: { form: UserForm; adding: boolean }) {
  return (
    <div className="field">
      <label htmlFor={INPUT_IDS.password}>{adding ? 'Initial password' : 'New password'}</label>
      <input
        id={INPUT_IDS.password}
        type="password"
        autoComplete="new-password"
        required
        maxLength={userCreateInPasswordOneMax}
        value={form.password}
        {...fieldA11y(INPUT_IDS.password, form.server.password, 'user-password-hint')}
        onChange={(e) => form.setPassword(e.target.value)}
      />
      <span className="hint" id="user-password-hint">{PASSWORD_HINT}</span>
      <FieldError inputId={INPUT_IDS.password} message={form.server.password} />
    </div>
  );
}

export function ResetPasswordDialog({ user, onClose }: { user: UserAccount; onClose: () => void }) {
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const invalid = Object.keys(fieldErrors(PasswordResetIn, { password })).length > 0;
  const server = serverFieldErrors(reset.error);

  return (
    <Modal
      title="Reset password"
      subtitle={`${user.display_name} · ${user.email}`}
      size="narrow"
      onClose={onClose}
      onSubmit={() => reset.mutate({ id: user.id, password }, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(reset)} label="Reset password" disabled={invalid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(reset.error, Object.keys(PasswordResetIn.shape))} />
        <div className="field">
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            maxLength={passwordResetInPasswordMax}
            value={password}
            {...fieldA11y('reset-password', server.password, 'reset-password-hint')}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="hint" id="reset-password-hint">{PASSWORD_HINT} Their existing sessions end.</span>
          <FieldError inputId="reset-password" message={server.password} />
        </div>
      </div>
    </Modal>
  );
}
